import { ALL_VALUE, INVALID_VALUE } from '../API/ItemsGetters'
import { SelectOption, SelectOptions } from '../Components/Selects/Types'
import { hasBusinessIntegrationOption, parseBusinessIntegrationOptions } from './CxProductApplicability'
import { BUSINESS_OPTION } from '../../TelSysConfiguration/Constants/Constants'
import { CXScoreLicences } from '../../../NonReact/constants/CXScoreLicences'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { isCxsCompatibleTelSys } from '../../../NonReact/utils/CxsSubscriptionAccessUtils'
import { isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'

/**
 * Whether a TelSys or partition field names one specific entity: not empty, all ({@link ALL_VALUE}) or unknown
 * ({@link INVALID_VALUE}). ID 0 is a real entity, such as the Common Devices partition.
 *
 * @param value - TelSys or partition ID from the form.
 * @returns True when the value names one specific entity.
 */
function isConcreteSelection(value: Nullable<number> | undefined): value is number {
    return isVarNotEmpty(value) && value !== ALL_VALUE && value !== INVALID_VALUE
}

/**
 * Add-user CXS scope: exactly one concrete permissions partition (not all/none).
 *
 * @param partitionIds - Value of the permissions partition field (array of partition IDs in TelSys).
 * @returns True when CXS subscription may be offered for this scope.
 */
export function isCxsSubscriptionScopeAllowedForAddUser(partitionIds: Nullable<number[]> | undefined): boolean {
    if (!partitionIds || partitionIds.length !== 1) return false

    const partId: number = partitionIds[0]

    return partId !== ALL_VALUE && partId !== INVALID_VALUE
}

/**
 * Whether the permissions partition field means all partitions: nothing selected, or the {@link ALL_VALUE} sentinel.
 * Several concrete partitions or {@link INVALID_VALUE} do not count.
 *
 * @param partitionIds - Value of the permissions partition field (array of partition IDs in TelSys).
 * @returns True when scope covers all partitions of the TelSys.
 */
export function isAllPartitionsScope(partitionIds: Nullable<number[]> | undefined): boolean {
    if (!partitionIds || partitionIds.length === 0) return true

    return partitionIds.length === 1 && partitionIds[0] === ALL_VALUE
}

/** Inputs for {@link isCxsSubscriptionScopeAllowedForTelSys}. */
type AddUserCxsScopeArgs = {
    /** Value of the permissions TelSys field. */
    telSysValue: Nullable<number> | undefined
    /** Value of the permissions partition field (array of partition IDs in TelSys). */
    permissionPartitionIds: Nullable<number[]> | undefined
    /** Value of the endpoint partition field (partition ID in TelSys). */
    endpointPartitionId: Nullable<number> | undefined
    /** Resolved telephony server type for the user. */
    telSysType: Nullable<TelSysTypes>
}

/**
 * Add-user CXS scope for a telephony server type. A single concrete partition is always accepted and several
 * concrete partitions never are. An all-partitions scope is accepted:
 * - For MS Teams, since the backend ({@code CReqRespUserBase.validateCXSAgainstTelSysAndPartition}) has no
 *   partition check for Teams.
 * - For BroadWorks (or a Simulator emulating it) once a concrete endpoint partition is chosen. The backend needs the
 *   user's partition to be Enterprise-licensed and takes it from the endpoint before the permissions scope
 *   ({@code CReqRespUserBase.getPartitionIDForUser}); that licence is applied through
 *   {@link cxsPartitionLicencesForAddUser}. The permissions TelSys must be concrete, as the backend reads the TelSys
 *   from the permissions scope, never the endpoint.
 *
 * @param args - Permissions TelSys and partitions, endpoint partition and resolved telephony server type.
 * @returns True when CXS subscription may be offered for this scope.
 */
export function isCxsSubscriptionScopeAllowedForTelSys({
    telSysValue,
    permissionPartitionIds,
    endpointPartitionId,
    telSysType
}: AddUserCxsScopeArgs): boolean {
    if (isCxsSubscriptionScopeAllowedForAddUser(permissionPartitionIds)) return true

    if (!isAllPartitionsScope(permissionPartitionIds)) return false

    if (telSysType === TelSysTypes.MS_TEAMS) return true

    return isConcreteSelection(telSysValue) && isConcreteSelection(endpointPartitionId)
}

/** Form values that decide which partition a CXS licence is validated against. */
export type CxsPartitionSelection = {
    /** Value of the permissions TelSys field. */
    telSysValue: Nullable<number> | undefined
    /** Value of the permissions partition field (array of partition IDs in TelSys). */
    permissionPartitionIds: Nullable<number[]> | undefined
    /** Value of the endpoint TelSys field. */
    endpointTelSysValue: Nullable<number> | undefined
    /** Value of the endpoint partition field (partition ID in TelSys). */
    endpointPartitionId: Nullable<number> | undefined
}

/** A partition, identified by its TelSys and its ID in that TelSys (the ID alone is only unique within a TelSys). */
export type CxsPartition = {
    readonly telSysId: number
    readonly partitionId: number
}

/**
 * The partition a CXS licence is validated against, mirroring {@code CReqRespUserBase.getPartitionIDForUser}: the
 * endpoint partition when concrete, else the first permissions partition, which is the one the backend takes when
 * there are several.
 *
 * @param selection - Permissions and endpoint TelSys and partition values.
 * @returns The TelSys and partition, or null when the user resolves to no partition.
 */
export function resolveCxsPartition({
    telSysValue,
    permissionPartitionIds,
    endpointTelSysValue,
    endpointPartitionId
}: CxsPartitionSelection): Nullable<CxsPartition> {
    if (isConcreteSelection(endpointTelSysValue) && isConcreteSelection(endpointPartitionId)) {
        return { telSysId: endpointTelSysValue, partitionId: endpointPartitionId }
    }

    const permissionPartitionId: Definable<number> = permissionPartitionIds?.[0]

    if (!isConcreteSelection(telSysValue) || !isConcreteSelection(permissionPartitionId)) return null

    return { telSysId: telSysValue, partitionId: permissionPartitionId }
}

/**
 * The reporting licence of a partition, from a list of partition options.
 *
 * @param partitionOptions - Partition options, each carrying its licence.
 * @param partitionId - Partition ID in TelSys to look up.
 * @returns The partition's licence, or undefined when it is not listed or carries none.
 */
function findPartitionLicence(partitionOptions: SelectOptions<number>, partitionId: number): Definable<string> {
    return partitionOptions.find((option: SelectOption<number>): boolean => option.value === partitionId)
        ?.licencedForRptType
}

/**
 * Whether two form states resolve to different CXS partitions (see {@link resolveCxsPartition}). Filling the endpoint
 * partition in from a single permissions partition is not a change, and the same partition ID on another TelSys is.
 *
 * @param loaded - TelSys and partition values the user was loaded with.
 * @param current - Current TelSys and partition values.
 * @returns True when the user now resolves to a different partition, or to none.
 */
export function hasCxsPartitionChanged(loaded: CxsPartitionSelection, current: CxsPartitionSelection): boolean {
    const loadedPartition: Nullable<CxsPartition> = resolveCxsPartition(loaded)
    const currentPartition: Nullable<CxsPartition> = resolveCxsPartition(current)

    return (
        loadedPartition?.telSysId !== currentPartition?.telSysId ||
        loadedPartition?.partitionId !== currentPartition?.partitionId
    )
}

/**
 * A TelSys or partition field value as one specific entity, or null for all, unknown or nothing.
 *
 * @param value - TelSys or partition ID from the form.
 * @returns The ID when it names one specific entity, else null.
 */
function concreteSelectionOrNull(value: Nullable<number> | undefined): Nullable<number> {
    return isConcreteSelection(value) ? value : null
}

/**
 * Whether the user was moved off what the backend validated a loaded CXS licence against: the permissions TelSys,
 * the first permissions partition (which decides CXS Agent, see {@link isCxsLicenceRejectedForSetup}), or the
 * partition the licence resolves to (see {@link hasCxsPartitionChanged}). All, unknown and none count as the same.
 *
 * @param loaded - TelSys and partition values the user was loaded with.
 * @param current - Current TelSys and partition values.
 * @returns True when the user is now on another TelSys or partition.
 */
export function hasCxsContextChanged(loaded: CxsPartitionSelection, current: CxsPartitionSelection): boolean {
    return (
        concreteSelectionOrNull(loaded.telSysValue) !== concreteSelectionOrNull(current.telSysValue) ||
        concreteSelectionOrNull(loaded.permissionPartitionIds?.[0]) !==
            concreteSelectionOrNull(current.permissionPartitionIds?.[0]) ||
        hasCxsPartitionChanged(loaded, current)
    )
}

/** Inputs for {@link isCxsLicenceRejectedForSetup}. */
type CxsLicenceRejectionArgs = {
    /** CXS licence being validated. */
    cxsLicence: Definable<string>
    /** Current TelSys and partition values. */
    selection: CxsPartitionSelection
    /** Resolved telephony server type for the user, or {@link TelSysTypes.UNKNOWN} / null while it loads. */
    telSysType: Nullable<TelSysTypes>
    /** The TelSys {@code IntegrationModeOptions}: undefined until loaded, null when it has none. Read for MS Teams. */
    integrationModeOptions: Nullable<string> | undefined
    /** The TelSys {@code CallControlOptions}, or null while not known. Read for a Simulator. */
    callControlOptions: Nullable<string> | undefined
    /** Licence of the partition the user resolves to (see {@link resolveCxsPartition}), when known. */
    partitionLicence: Definable<string>
}

/**
 * Whether the backend is certain to reject a CXS licence for the user as now set up, mirroring
 * {@code CReqRespUserBase.validateCXSAgainstTelSysAndPartition} and the scope rule for CXS Agent in
 * {@code CReqRespUserBase.validateCXSLicenseAgainstDevice}. Anything not known yet counts as accepted, so the backend
 * keeps the final say:
 * - All TelSyss, or a known TelSys type without CX Score such as Webex Calling: error 11410.
 * - CXS Agent whose first permissions partition is All, on any TelSys type: error 11267. Several concrete partitions
 *   pass this rule, as the backend only checks the first.
 * - MS Teams whose integration options have loaded without the CX-Score option: error 11420.
 * - A Simulator whose call control options are known not to emulate BroadWorks: error 11410.
 * - BroadWorks or a Simulator with no partition to validate, or one whose licence is known not to be Enterprise:
 *   error 11413.
 *
 * @param args - CXS licence, current TelSys and partition values, TelSys type and options, and the partition licence.
 * @returns True when saving the CXS licence for this setup is certain to fail.
 */
export function isCxsLicenceRejectedForSetup({
    cxsLicence,
    selection,
    telSysType,
    integrationModeOptions,
    callControlOptions,
    partitionLicence
}: CxsLicenceRejectionArgs): boolean {
    if (!isConcreteSelection(selection.telSysValue)) return true

    if (cxsLicence === CXScoreLicences.CXS_AGENT && !isConcreteSelection(selection.permissionPartitionIds?.[0])) {
        return true
    }

    if (!isVarNotEmpty(telSysType) || telSysType === TelSysTypes.UNKNOWN) return false

    if (telSysType === TelSysTypes.MS_TEAMS) {
        return (
            integrationModeOptions !== undefined &&
            !hasBusinessIntegrationOption(
                parseBusinessIntegrationOptions(integrationModeOptions),
                BUSINESS_OPTION.CX_SCORE
            )
        )
    }

    if (telSysType !== TelSysTypes.BROADSOFT_BROADWORKS && telSysType !== TelSysTypes.SIMULATOR) return true

    const isKnownNotToEmulateBroadWorks: boolean =
        telSysType === TelSysTypes.SIMULATOR &&
        isVarNotEmpty(callControlOptions) &&
        !isCxsCompatibleTelSys(telSysType, callControlOptions)

    if (isKnownNotToEmulateBroadWorks || !isVarNotEmpty(resolveCxsPartition(selection))) return true

    return isStringNotEmpty(partitionLicence) && partitionLicence !== ReportingAccessLevels.ENTERPRISE
}

/** Inputs for {@link isLoadedCxsLicenceRejectedAfterMove}. */
type LoadedCxsLicenceRejectionArgs = Omit<CxsLicenceRejectionArgs, 'selection' | 'partitionLicence'> & {
    /** TelSys and partition values the user was loaded with. */
    loaded: CxsPartitionSelection
    /** Current TelSys and partition values. */
    current: CxsPartitionSelection
    /** Partition options of the endpoint TelSys, each carrying its licence. */
    endpointPartitionOptions: SelectOptions<number>
}

/**
 * Whether the user being edited was moved off the TelSys or partition their CXS licence was validated against (see
 * {@link hasCxsContextChanged}) to somewhere the backend is certain to reject it (see
 * {@link isCxsLicenceRejectedForSetup}). A licence the user has not been moved away from is left alone, whatever the
 * backend would now say about it.
 *
 * @param args - Loaded and current TelSys and partition values, TelSys type and options, and the endpoint partitions.
 * @returns True when the loaded licence cannot be saved where the user now is.
 */
export function isLoadedCxsLicenceRejectedAfterMove({
    cxsLicence,
    loaded,
    current,
    endpointPartitionOptions,
    telSysType,
    integrationModeOptions,
    callControlOptions
}: LoadedCxsLicenceRejectionArgs): boolean {
    if (!hasCxsContextChanged(loaded, current)) return false

    const partition: Nullable<CxsPartition> = resolveCxsPartition(current)

    // The options list the partitions of the endpoint TelSys, so a partition on another TelSys has no known licence.
    const partitionLicence: Definable<string> =
        isVarNotEmpty(partition) && partition.telSysId === current.endpointTelSysValue
            ? findPartitionLicence(endpointPartitionOptions, partition.partitionId)
            : undefined

    return isCxsLicenceRejectedForSetup({
        cxsLicence,
        selection: current,
        telSysType,
        integrationModeOptions,
        callControlOptions,
        partitionLicence
    })
}

/** Inputs for {@link cxsPartitionLicencesForAddUser}. */
type AddUserCxsPartitionLicencesArgs = {
    /** Partition options of the endpoint TelSys, each carrying its licence. */
    endpointPartitionOptions: SelectOptions<number>
    /** Value of the endpoint partition field (partition ID in TelSys). */
    endpointPartitionId: Nullable<number> | undefined
    /** Licences held across the TelSys partitions, when known. */
    telSysPartitionLicences: Nullable<readonly string[]>
    /**
     * Whether the user being edited holds a CXS licence other than None and is still on the TelSys and partition it
     * was loaded with (see {@link hasCxsContextChanged}).
     */
    keepsLoadedCxsLicence: boolean
}

/**
 * Partition licences that gate CXS in the add-user modal (see {@code isCxsProductApplicable}). BroadWorks needs the
 * user's partition to be Enterprise-licensed, and the backend takes that partition from the endpoint before the
 * permissions scope ({@code CReqRespUserBase.getPartitionIDForUser}). The endpoint partition follows a single
 * permissions partition, so once its licence is known it replaces the TelSys-wide set.
 *
 * The TelSys-wide set is kept while the endpoint partition or its licence is unknown, and for a user who holds a CXS
 * licence on the partition they were loaded with: the backend revalidates CXS on every change, so that licence must
 * stay visible to be removed. Once the user is moved to another partition, that partition's licence applies.
 *
 * @param args - Endpoint partition options and value, TelSys-wide licences and whether a loaded licence is kept.
 * @returns The endpoint partition's licence alone, or the TelSys-wide licences.
 */
export function cxsPartitionLicencesForAddUser({
    endpointPartitionOptions,
    endpointPartitionId,
    telSysPartitionLicences,
    keepsLoadedCxsLicence
}: AddUserCxsPartitionLicencesArgs): Nullable<readonly string[]> {
    if (keepsLoadedCxsLicence || !isConcreteSelection(endpointPartitionId)) return telSysPartitionLicences

    const endpointPartitionLicence: Definable<string> = findPartitionLicence(
        endpointPartitionOptions,
        endpointPartitionId
    )

    return isStringNotEmpty(endpointPartitionLicence) ? [endpointPartitionLicence] : telSysPartitionLicences
}

/**
 * Whether scope uses exactly one concrete partition.
 *
 * @param partitionIds - Value of the permissions partition field.
 * @returns True when the selected partition is concrete.
 */
export function isCxsSubscriptionScopeAllowedForPermissionsPartition(
    partitionIds: Nullable<number[]> | undefined
): boolean {
    return isCxsSubscriptionScopeAllowedForAddUser(partitionIds)
}

/**
 * Whether the telephony server type supports CXS subscriptions.
 *
 * CX Score is offered for BroadWorks and MS Teams telephony servers only.
 *
 * @param telSysType - Resolved telephony server type for the user.
 * @returns True when CXS subscription may be offered for this telephony server type.
 */
export function isCxsTelSysSupported(telSysType: Nullable<TelSysTypes>): boolean {
    return telSysType === TelSysTypes.BROADSOFT_BROADWORKS || telSysType === TelSysTypes.MS_TEAMS
}

type AddUserCxsApplicabilityPendingArgs = {
    selectedUser?: string
    isCXScoreFeatureEnabled: Nullable<boolean>
    permissionPartitionIds: Nullable<number[]> | undefined
    endpointPartitionId?: Nullable<number>
    telSysValue: Nullable<number> | undefined
    telSysType: Nullable<TelSysTypes>
    telSysIntegrationModeOptions?: Nullable<string>
}

/**
 * Whether edit-mode CXS applicability is still being determined.
 *
 * For MS Teams servers the integration type and mode also gate CXS, so applicability stays pending until both
 * have loaded ({@code undefined} means not yet fetched; {@code null} means fetched with no value reported).
 *
 * An all-partitions scope stays pending while the tel-sys type is unknown, since whether it qualifies depends on the
 * type (see {@link isCxsSubscriptionScopeAllowedForTelSys}).
 *
 * @param args - Applicability inputs from the add-user modal.
 * @returns True when scope is concrete enough to support CXS, but feature-flag, tel-sys type or Teams
 *     integration data has not loaded yet.
 */
export function isCxsSubscriptionApplicabilityPendingForAddUser({
    selectedUser,
    isCXScoreFeatureEnabled,
    permissionPartitionIds,
    endpointPartitionId,
    telSysValue,
    telSysType,
    telSysIntegrationModeOptions
}: AddUserCxsApplicabilityPendingArgs): boolean {
    if (!selectedUser) return false

    const isScopeAllowed: boolean =
        isCxsSubscriptionScopeAllowedForTelSys({
            telSysValue,
            permissionPartitionIds,
            endpointPartitionId,
            telSysType
        }) ||
        (telSysType === TelSysTypes.UNKNOWN && isAllPartitionsScope(permissionPartitionIds))

    if (!isScopeAllowed) return false

    if (!telSysValue || telSysValue === ALL_VALUE || telSysValue === INVALID_VALUE) return false

    if (isCXScoreFeatureEnabled === false) return false

    return (
        isCXScoreFeatureEnabled === undefined ||
        telSysType === TelSysTypes.UNKNOWN ||
        // CXS on MS Teams hinges on the business integration options, which load with the telsys.
        (telSysType === TelSysTypes.MS_TEAMS && telSysIntegrationModeOptions === undefined)
    )
}
